import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from '../generated/web-bff';
import { toApiError } from '../problem';
import type {
  Activation,
  ActivationRequest,
  AdherenceTrend,
  AdherenceTrendQuery,
  AdherenceWeek,
  AttentionSummary,
  CareTeam,
  ClientDetail,
  CarerMatchList,
  ClientPage,
  DevicePage,
  DoseWindows,
  ListClientsQuery,
  ListDevicesQuery,
  Me,
  MedicationProfile,
} from './types';

export interface WebApiOptions {
  /**
   * Where the Web MMS BFF is served: `/api/web/v1` behind Front Door, or a mock server's root. Relative values are
   * resolved against the page's origin.
   */
  baseUrl: string;
  /** Supplies the bearer token for each request; null sends none (the API then answers 401). */
  getAccessToken: () => Promise<string | null>;
  /** Called when the API refuses the token, so the shell can end the session. */
  onUnauthorized?: () => void;
  /** Replaces the global fetch, for tests. */
  fetch?: (request: Request) => Promise<Response>;
}

/** The Web MMS BFF operations the front end uses, typed from the contract. Each throws ApiError on failure. */
export interface WebApi {
  getMe(signal?: AbortSignal): Promise<Me>;
  getAttentionSummary(signal?: AbortSignal): Promise<AttentionSummary>;
  listClients(query: ListClientsQuery, signal?: AbortSignal): Promise<ClientPage>;
  getClient(clientId: string, signal?: AbortSignal): Promise<ClientDetail>;
  getClientAdherenceWeek(clientId: string, weekStart?: string, signal?: AbortSignal): Promise<AdherenceWeek>;
  getClientCareTeam(clientId: string, signal?: AbortSignal): Promise<CareTeam>;
  getClientAdherenceTrend(
    clientId: string,
    query: AdherenceTrendQuery,
    signal?: AbortSignal,
  ): Promise<AdherenceTrend>;
  getClientMedicationProfile(clientId: string, signal?: AbortSignal): Promise<MedicationProfile>;
  getClientDoseWindows(clientId: string, signal?: AbortSignal): Promise<DoseWindows>;
  listCarerMatches(mobile: string, signal?: AbortSignal): Promise<CarerMatchList>;
  /** `idempotencyKey` is generated once per activation attempt by the caller, so a retry reuses it. */
  createActivation(request: ActivationRequest, idempotencyKey: string): Promise<Activation>;
  getActivation(activationId: string, signal?: AbortSignal): Promise<Activation>;
  listDevices(query: ListDevicesQuery, signal?: AbortSignal): Promise<DevicePage>;
}

function absolute(baseUrl: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return new URL(baseUrl, origin).toString().replace(/\/$/, '');
}

function newCorrelationId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

type Result<T> = { data?: T; error?: unknown; response: Response };

async function unwrap<T>(call: Promise<Result<T>>): Promise<T> {
  const { data, error, response } = await call;
  if (!response.ok || error !== undefined) throw toApiError(response, error);
  return data as T;
}

export function createWebApi(options: WebApiOptions): WebApi {
  const client = createClient<paths>({
    baseUrl: absolute(options.baseUrl),
    ...(options.fetch && { fetch: options.fetch }),
  });

  const auth: Middleware = {
    async onRequest({ request }) {
      const token = await options.getAccessToken();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      request.headers.set('X-Correlation-Id', newCorrelationId());
      return request;
    },
    onResponse({ response }) {
      if (response.status === 401) options.onUnauthorized?.();
      return response;
    },
  };
  client.use(auth);

  return {
    getMe: (signal) => unwrap(client.GET('/me', { signal })),
    getAttentionSummary: (signal) => unwrap(client.GET('/attention-summary', { signal })),
    listClients: (query, signal) => unwrap(client.GET('/clients', { params: { query }, signal })),
    getClient: (clientId, signal) =>
      unwrap(client.GET('/clients/{clientId}', { params: { path: { clientId } }, signal })),
    getClientAdherenceWeek: (clientId, weekStart, signal) =>
      unwrap(
        client.GET('/clients/{clientId}/adherence', {
          params: { path: { clientId }, query: weekStart ? { weekStart } : {} },
          signal,
        }),
      ),
    getClientCareTeam: (clientId, signal) =>
      unwrap(client.GET('/clients/{clientId}/care-team', { params: { path: { clientId } }, signal })),
    getClientAdherenceTrend: (clientId, query, signal) =>
      unwrap(
        client.GET('/clients/{clientId}/adherence/trend', { params: { path: { clientId }, query }, signal }),
      ),
    getClientMedicationProfile: (clientId, signal) =>
      unwrap(
        client.GET('/clients/{clientId}/medication-profile', { params: { path: { clientId } }, signal }),
      ),
    getClientDoseWindows: (clientId, signal) =>
      unwrap(client.GET('/clients/{clientId}/dose-windows', { params: { path: { clientId } }, signal })),
    listCarerMatches: (mobile, signal) =>
      unwrap(client.GET('/carer-matches', { params: { query: { mobile } }, signal })),
    createActivation: (request, idempotencyKey) =>
      unwrap(
        client.POST('/activations', {
          params: { header: { 'Idempotency-Key': idempotencyKey } },
          body: request,
        }),
      ),
    getActivation: (activationId, signal) =>
      unwrap(client.GET('/activations/{activationId}', { params: { path: { activationId } }, signal })),
    listDevices: (query, signal) => unwrap(client.GET('/devices', { params: { query }, signal })),
  };
}
